import { differenceInMinutes } from "date-fns"

import type { Feedback, Workspace } from "@productlane/zero"
import {
	isLeftoverDeadline,
	owesReplyAgainstDeadline,
} from "@productlane/lib/src/sla-window"

export const SLA_DURATIONS = [
	{ label: "12 hours", minutes: 720 },
	{ label: "24 hours", minutes: 1440 },
	{ label: "48 hours", minutes: 2880 },
	{ label: "1 week", minutes: 10080 },
	{ label: "2 weeks", minutes: 20160 },
	{ label: "4 weeks", minutes: 40320 },
]

export type SlaStatus =
	| "lowRisk"
	| "mediumRisk"
	| "highRisk"
	| "breached"
	| "achieved"
	| "failed"

export const SLA_STATUS_LABELS: Record<SlaStatus, string> = {
	lowRisk: "Low risk",
	mediumRisk: "Medium risk",
	highRisk: "High risk",
	breached: "Breached",
	achieved: "Achieved",
	failed: "Failed",
}

export const SLA_STATUS_DEFINITIONS: Record<SlaStatus, string> = {
	lowRisk: "More than 1 week away from SLA",
	mediumRisk: "Within 1 week of SLA",
	highRisk: "Within 1 day of SLA",
	breached: "SLA has passed",
	achieved: "Issue was completed within SLA",
	failed: "Issue was completed after SLA was breached",
}

const MINUTES_PER_DAY = 24 * 60
const MINUTES_PER_WEEK = 7 * MINUTES_PER_DAY

export function formatTimeRemaining(deadline: Date): string {
	const now = new Date()
	const totalMinutes = Math.abs(differenceInMinutes(deadline, now))

	if (totalMinutes < 60) {
		return `${totalMinutes}m`
	}

	const hours = Math.floor(totalMinutes / 60)
	if (hours < 24) {
		const mins = totalMinutes % 60
		return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
	}

	const days = Math.floor(hours / 24)
	const remainingHours = hours % 24
	return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`
}

export function computeSlaDeadline(
	thread: Pick<
		Feedback,
		| "slaDeadlineAt"
		| "state"
		| "lastStateChangeAt"
		| "replied"
		| "lastInboundMessageAt"
		| "lastOutboundMessageAt"
	>,
): { deadline: Date; status: SlaStatus } | null {
	// slaDeadlineAt === 0 means explicitly removed
	if (thread.slaDeadlineAt === 0) return null
	if (!thread.slaDeadlineAt) return null

	const deadline = new Date(thread.slaDeadlineAt)
	const slaWindow = {
		replied: thread.replied,
		slaDeadlineAt: thread.slaDeadlineAt,
		lastInboundMessageAt: thread.lastInboundMessageAt,
		lastOutboundMessageAt: thread.lastOutboundMessageAt,
	}

	// Completed thread → achieved or failed (one-shot end states). A response
	// deadline is judged by when the reply went out, not when someone hit Done.
	if (thread.state === "PROCESSED" && thread.lastStateChangeAt) {
		if (isLeftoverDeadline(slaWindow)) return null
		const lastInbound = thread.lastInboundMessageAt
		const metByReply =
			!!thread.lastOutboundMessageAt &&
			thread.lastOutboundMessageAt <= thread.slaDeadlineAt &&
			(!lastInbound || thread.lastOutboundMessageAt >= lastInbound)
		return {
			deadline,
			status:
				metByReply || thread.lastStateChangeAt <= thread.slaDeadlineAt
					? "achieved"
					: "failed",
		}
	}

	if (!owesReplyAgainstDeadline(slaWindow)) return null

	const now = Date.now()
	if (now >= thread.slaDeadlineAt) return { deadline, status: "breached" }

	const minutesRemaining = (thread.slaDeadlineAt - now) / (1000 * 60)
	if (minutesRemaining <= MINUTES_PER_DAY)
		return { deadline, status: "highRisk" }
	if (minutesRemaining <= MINUTES_PER_WEEK)
		return { deadline, status: "mediumRisk" }
	return { deadline, status: "lowRisk" }
}

export function hasSla(
	thread: Pick<Feedback, "slaDeadlineAt"> | null | undefined,
	workspace: Pick<Workspace, "slaEnabled"> | null | undefined,
): boolean {
	if (!thread || !workspace?.slaEnabled) return false
	if (thread.slaDeadlineAt === 0) return false
	return !!thread.slaDeadlineAt
}
